import { useState, useEffect, useRef, useCallback } from 'react';

export const DEFAULT_PROCESSING_STEPS = [
  'Analyzing your content...',
  'Detecting source...',
  'Fetching content...',
  'Extracting transcript...',
  'Almost there...',
];

interface UseProcessingStepsOptions {
  isActive: boolean;
  steps?: string[];
  intervalMs?: number;
}

interface UseProcessingStepsReturn {
  currentStepIndex: number;
  currentStep: string;
  isProcessing: boolean;
}

export function useProcessingSteps({
  isActive,
  steps = DEFAULT_PROCESSING_STEPS,
  intervalMs = 2500,
}: UseProcessingStepsOptions): UseProcessingStepsReturn {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      setCurrentStepIndex(0);

      intervalRef.current = setInterval(() => {
        setCurrentStepIndex((prev) => (prev + 1) % steps.length);
      }, intervalMs);
    } else {
      clearTimer();
      setCurrentStepIndex(0);
    }

    return clearTimer;
  }, [isActive, steps.length, intervalMs, clearTimer]);

  return {
    currentStepIndex,
    currentStep: steps[currentStepIndex] || steps[0],
    isProcessing: isActive,
  };
}
